import React, { useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import AppLayout from '../components/AppLayout';
import { AdBannerResponsive } from '../components/GoogleAdsense';
import ProductCarousel from '../components/affiliate/ProductCarousel';
import '../styles/pages/UsagePolicy.css';

const UsagePolicy = () => {
  const navigate = useNavigate();

  // ページタイトル設定
  useEffect(() => {
    document.title = 'プランと料金 | つむぎ';
  }, []);

  return (
    <AppLayout>
      <div className="usage-policy-container">
        {/* ヘッダー */}
        <div className="usage-policy-header">
          <button
            onClick={() => navigate(-1)}
            className="usage-policy-back-button"
          >
            ← 戻る
          </button>
          <h1 className="usage-policy-title">
            プランと料金について
          </h1>
          <p className="usage-policy-subtitle">
            あなたに合ったプランをお選びください。<br />
            いつでもプラン変更・解約が可能です。
          </p>
        </div>

        {/* プランでできること */}
        <section className="usage-policy-section">
          <h2 className="usage-policy-section-title">
            各プランでできること
          </h2>

          <div className="usage-policy-info-box">
            <p className="usage-policy-info-text">
              すべてのプランで以下の機能をご利用いただけます。<br />
              プランによって、月間のご利用回数が異なります。
            </p>
          </div>

          <div className="usage-policy-features-grid">
            {[
              {
                icon: '💬',
                name: 'AI栄養相談',
                description: '体調や症状に合わせた食事アドバイス'
              },
              {
                icon: '🍳',
                name: 'レシピ生成',
                description: 'あなたに合ったレシピを自動生成'
              },
              {
                icon: '📸',
                name: '食材・成分表スキャン',
                description: '写真から食材や成分をチェック'
              },
              {
                icon: '🩸',
                name: '血液検査分析',
                description: '検査結果から健康アドバイス（Standard以上）'
              },
              {
                icon: '💭',
                name: 'つむぎチャット',
                description: '日常的な会話で心のサポート'
              }
            ].map((feature, index) => (
              <div key={index} className="usage-policy-feature-card">
                <div className="usage-policy-feature-content">
                  <span className="usage-policy-feature-icon">{feature.icon}</span>
                  <div className="usage-policy-feature-details">
                    <h3 className="usage-policy-feature-name">
                      {feature.name}
                    </h3>
                    <p className="usage-policy-feature-description">
                      {feature.description}
                    </p>
                  </div>
                </div>
              </div>
            ))}
          </div>
        </section>

        {/* 各機能の利用コスト */}
        <section className="usage-policy-section">
          <h2 className="usage-policy-section-title">
            各機能の利用コスト
          </h2>

          <div className="usage-policy-info-box">
            <p className="usage-policy-info-text-with-margin">
              各プランには月間ご利用いただける「ポイント」が割り当てられています。<br />
              機能を利用すると、その機能に応じたポイントが消費されます。<br />
              <span className="usage-policy-info-note">（1ポイント = 約1円相当のAPI利用コスト）</span>
            </p>

            <div className="usage-policy-cost-grid">
              {[
                { icon: '💬', name: 'AI栄養相談', cost: '4pt/回', description: '体調や症状に合わせた食事アドバイス' },
                { icon: '🍳', name: 'レシピ生成', cost: '4pt/回', description: 'あなたに合ったレシピを自動生成' },
                { icon: '📸', name: '成分表スキャン', cost: '5pt/回', description: '写真から食材や成分をチェック' },
                { icon: '🩸', name: '血液検査分析', cost: '8pt/回', description: '検査結果から健康アドバイス（Standard以上）' },
                { icon: '💭', name: 'つむぎチャット', cost: '1pt/回', description: '日常的な会話で心のサポート' },
                { icon: '🥗', name: '冷蔵庫レシピ', cost: '10pt/回', description: '冷蔵庫の写真からレシピ提案' },
                { icon: '📰', name: 'チラシレシピ', cost: '10pt/回', description: 'スーパーのチラシからお得レシピ' },
                { icon: '🔍', name: '成分チェック', cost: '5pt/回', description: '食品の成分を橋本病視点でチェック' }
              ].map((feature, index) => (
                <div key={index} className="usage-policy-cost-item">
                  <div className="usage-policy-cost-left">
                    <span className="usage-policy-cost-icon">{feature.icon}</span>
                    <div className="usage-policy-cost-details">
                      <div className="usage-policy-cost-name">
                        {feature.name}
                      </div>
                      <div className="usage-policy-cost-description">
                        {feature.description}
                      </div>
                    </div>
                  </div>
                  <div className="usage-policy-cost-value">
                    {feature.cost}
                  </div>
                </div>
              ))}
            </div>
          </div>
        </section>

        {/* プラン詳細 */}
        <section className="usage-policy-section">
          <h2 className="usage-policy-section-title">
            プラン詳細
          </h2>

          {[
            {
              name: 'Freeプラン',
              price: '¥0/月',
              points: 50,
              recommend: 'まずはお試しで始めたい方',
              estimates: [
                'AI栄養相談なら 最大12回',
                'レシピ生成なら 最大12回',
                '成分表スキャンなら 最大10回',
                '血液検査分析：利用不可',
                'つむぎチャットなら 最大50回',
                '',
                '※ 各機能を組み合わせて50ポイントまでご利用可能'
              ]
            },
            {
              name: 'Standardプラン',
              price: '¥480/月',
              points: 900,
              recommend: '週2-3回利用される方におすすめ',
              estimates: [
                'AI栄養相談なら 最大225回',
                'レシピ生成なら 最大225回',
                '成分表スキャンなら 最大180回',
                '血液検査分析なら 最大112回',
                '',
                '※ 各機能を組み合わせて900ポイントまでご利用可能'
              ],
              popular: true
            },
            {
              name: 'Premiumプラン',
              price: '¥980/月',
              points: 1800,
              recommend: 'ほぼ毎日利用される方におすすめ',
              estimates: [
                'AI栄養相談なら 最大450回',
                'レシピ生成なら 最大450回',
                '成分表スキャンなら 最大360回',
                '血液検査分析なら 最大225回',
                '',
                '※ 各機能を組み合わせて1,800ポイントまでご利用可能'
              ]
            },
            {
              name: 'Premium Plusプラン',
              price: '¥1,980/月',
              points: 3600,
              recommend: 'さらに充実したサポートが必要な方',
              estimates: [
                'AI栄養相談なら 最大900回',
                'レシピ生成なら 最大900回',
                '成分表スキャンなら 最大720回',
                '血液検査分析なら 最大450回',
                '',
                '※ 各機能を組み合わせて3,600ポイントまでご利用可能'
              ]
            },
            {
              name: 'Ultimateプラン',
              price: '¥2,980/月',
              points: 5500,
              recommend: '最上級の健康管理を求める方',
              estimates: [
                'AI栄養相談なら 最大1,375回',
                'レシピ生成なら 最大1,375回',
                '成分表スキャンなら 最大1,100回',
                '血液検査分析なら 最大687回',
                '',
                '※ 各機能を組み合わせて5,500ポイントまでご利用可能'
              ]
            },
          ].map((plan, index) => (
            <div key={index} className={plan.popular ? 'usage-policy-plan-card-popular' : 'usage-policy-plan-card'}>
              {plan.popular && (
                <div className="usage-policy-popular-badge">
                  人気
                </div>
              )}

              <div className="usage-policy-plan-header">
                <h3 className="usage-policy-plan-name">
                  {plan.name}
                </h3>
                <span className="usage-policy-plan-price">
                  {plan.price}
                </span>
              </div>

              <div className="usage-policy-plan-points">
                月間 {plan.points}ポイント
              </div>

              <div className="usage-policy-plan-recommend">
                {plan.recommend}
              </div>

              <div className="usage-policy-plan-estimates">
                <div className="usage-policy-plan-estimates-title">
                  ご利用目安
                </div>
                <ul className="usage-policy-plan-estimates-list">
                  {plan.estimates.map((estimate, i) => (
                    <li key={i}>{estimate}</li>
                  ))}
                </ul>
              </div>
            </div>
          ))}
        </section>

        <AdBannerResponsive />

        {/* よくある質問 */}
        <section className="usage-policy-section">
          <h2 className="usage-policy-section-title">
            よくある質問
          </h2>

          <div className="usage-policy-faq-grid">
            {[
              {
                q: 'プラン変更はいつでもできますか？',
                a: 'はい、いつでも可能です。アップグレードは即時反映、ダウングレードは翌月1日から適用されます。'
              },
              {
                q: '解約はいつでもできますか？',
                a: 'はい、いつでもマイページから解約できます。解約後も当月末まで有料機能をご利用いただけます。'
              },
              {
                q: '毎月リセットとは何ですか？',
                a: '毎月1日になると、ご利用回数がリセットされ、新しい月として再び全機能をご利用いただけます。前月の残りは繰り越されません。'
              },
              {
                q: '余ったポイントは翌月に繰り越されますか？',
                a: 'いいえ、ポイントは毎月1日にリセットされ、前月の残りポイントは繰り越されません。毎月新しいポイントが付与されます。'
              },
              {
                q: '今月の上限に達したらどうなりますか？',
                a: '翌月1日のリセットまで、一部機能のご利用を一時停止させていただきます。つむぎチャットなど一部機能は引き続きご利用可能です。'
              },
              {
                q: 'データは安全ですか？',
                a: 'はい、お客様の健康データは厳重に管理され、AI学習には使用されません。詳しくはプライバシーポリシーをご確認ください。'
              },
              {
                q: '支払い方法は何ですか？',
                a: 'クレジットカード（Visa、Mastercard、JCB、American Express）でのお支払いが可能です。'
              }
            ].map((faq, index) => (
              <div key={index} className="usage-policy-faq-card">
                <h3 className="usage-policy-faq-question">
                  Q. {faq.q}
                </h3>
                <p className="usage-policy-faq-answer">
                  A. {faq.a}
                </p>
              </div>
            ))}
          </div>
        </section>

        {/* お問い合わせ */}
        <section className="usage-policy-section">
          <div className="usage-policy-contact-box">
            <h3 className="usage-policy-contact-title">
              ご不明な点がございましたら
            </h3>
            <p className="usage-policy-contact-text">
              お気軽にお問い合わせください
            </p>
            <button
              onClick={() => navigate('/contact')}
              className="usage-policy-contact-button"
            >
              お問い合わせ
            </button>
          </div>
        </section>

        <AdBannerResponsive />
        <ProductCarousel location="usage-policy" />

        <div className="usage-policy-footer">
          最終更新: 2026年1月
        </div>
      </div>
    </AppLayout>
  );
};

export default UsagePolicy;
